import { useState, useEffect } from 'react';
import { Star } from 'lucide-react';

const testimonials = [
  {
    name: 'Rebecca Hartman',
    designation: 'CEO, Hartman & Associates',
    text: 'The trademark registration process was seamless and professional. The team guided us every step of the way, ensuring our brand was fully protected.',
    rating: 5,
  },
  {
    name: 'Marcus Johnson',
    designation: 'Founder, Tech Innovations Inc.',
    text: 'Exceptional service! They handled everything from the initial search to final registration. Our trademark was approved without any issues.',
    rating: 5,
  },
  {
    name: 'Jennifer Patel',
    designation: 'Managing Director, Patel Enterprises',
    text: 'Professional, efficient, and knowledgeable. I highly recommend their services for anyone looking to protect their intellectual property.',
    rating: 5,
  },
  {
    name: 'David Kowalski',
    designation: 'Owner, Kowalski LLC',
    text: 'Outstanding experience from start to finish. The dedicated case manager kept us informed throughout the entire process.',
    rating: 5,
  },
  {
    name: 'Amanda Sullivan',
    designation: 'Creative Director, Sullivan Design Studio',
    text: 'Worth every penny! The comprehensive search report gave us peace of mind, and the registration process was incredibly smooth.',
    rating: 5,
  },
  {
    name: 'Robert Chen',
    designation: 'President, Chen Global Trading',
    text: 'Their expertise in trademark law is unmatched. They helped us protect our brand across multiple categories and countries.',
    rating: 5,
  },
  {
    name: 'Sarah Williams',
    designation: 'VP Marketing, Williams Fashion Group',
    text: 'Fast, reliable, and professional. Our trademark was registered in record time, allowing us to launch our new product line confidently.',
    rating: 5,
  },
  {
    name: 'Michael Rodriguez',
    designation: 'Founder & CEO, Rodriguez Fitness',
    text: 'The attention to detail and personalized service exceeded our expectations. They truly care about protecting your brand.',
    rating: 5,
  },
  {
    name: 'Lisa Anderson',
    designation: 'Director, Anderson Real Estate',
    text: 'Clear communication, transparent pricing, and excellent results. I would not hesitate to recommend their services to anyone.',
    rating: 5,
  },
  {
    name: 'Thomas Mitchell',
    designation: 'Owner, Mitchell Consulting Group',
    text: 'From initial consultation to final registration, the entire process was smooth and stress-free. Highly professional team!',
    rating: 5,
  },
];

export const TestimonialSlider = ({ autoSlide = true }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsPerSlide, setItemsPerSlide] = useState(3);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 664) {
        setItemsPerSlide(1);
      } else {
        setItemsPerSlide(3);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!autoSlide) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 6000);

    return () => clearInterval(interval);
  }, [autoSlide]);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const getSlideWidth = () => {
    return 100 / itemsPerSlide;
  };

  const getVisibleTestimonials = () => {
    const result = [];
    for (let i = 0; i < itemsPerSlide; i++) {
      result.push(testimonials[(currentIndex + i) % testimonials.length]);
    }
    return result;
  };

  return (
    <div className="section relative bg-white" data-testid="testimonial-slider">
      <div className="container mx-auto">
        <div className="text-center mb-20">
          <div className="inline-block bg-[#06214c]/10 px-6 py-3 rounded-full mb-6">
            <span className="text-[#06214c] font-semibold text-lg">⭐ Client Success Stories</span>
          </div>
          <h2 className="text-5xl md:text-6xl font-bold text-[#06214c] mb-6" style={{ fontFamily: 'Playfair Display' }}>
            What Our Clients Say
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Join thousands of satisfied clients who have successfully protected their brands with our expert trademark services.
          </p>
        </div>

        <div className="relative">
          <div className="max-w-7xl mx-auto">
            <div className="grid gap-6 mb-12" style={{ gridTemplateColumns: `repeat(${itemsPerSlide}, 1fr)` }}>
              {getVisibleTestimonials().map((testimonial, index) => (
                <div
                  key={`${currentIndex}-${index}`}
                  className="bg-gradient-to-br from-white to-[#FFF5E6] rounded-3xl shadow-xl p-10 hover:shadow-2xl transition-all duration-300 border-2 border-[#D4A574]/30 flex flex-col relative overflow-hidden group animate-fade-in"
                >
                  {/* Decorative quote mark */}
                  <div className="absolute top-6 left-6 text-[#D4A574]/20 text-8xl font-serif leading-none" style={{ fontFamily: 'Playfair Display' }}>
                    "
                  </div>

                  {/* Star rating */}
                  <div className="flex justify-center mb-6 relative z-10">
                    <div className="flex gap-1 bg-[#06214c]/5 px-4 py-2 rounded-full">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="text-[#D4A574] fill-current" size={22} />
                      ))}
                    </div>
                  </div>

                  {/* Testimonial text */}
                  <div className="relative z-10 flex-grow">
                    <p className="text-lg text-gray-700 text-center mb-8 leading-relaxed font-medium">
                      {testimonial.text}
                    </p>
                  </div>

                  {/* Author info */}
                  <div className="text-center pt-6 border-t-2 border-[#D4A574]/20 relative z-10">
                    <div className="w-16 h-16 bg-gradient-to-br from-[#06214c] to-[#D4A574] rounded-full mx-auto mb-4 flex items-center justify-center text-white text-2xl font-bold shadow-lg">
                      {testimonial.name.charAt(0)}
                    </div>
                    <p className="font-bold text-[#06214c] text-xl mb-2" style={{ fontFamily: 'Playfair Display' }}>
                      {testimonial.name}
                    </p>
                    <p className="text-gray-600 text-base font-medium">{testimonial.designation}</p>
                  </div>

                  {/* Hover gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-br from-[#06214c]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
              ))}
            </div>
          </div>

          {/* Dot indicators */}
          <div className="flex justify-center mt-10 space-x-2">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`h-2.5 rounded-full transition-all duration-300 ${
                  index === currentIndex
                    ? 'w-16 bg-gradient-to-r from-[#06214c] to-[#D4A574]'
                    : 'w-2.5 bg-gray-300 hover:bg-gray-400'
                }`}
                data-testid={`testimonial-dot-${index}`}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestimonialSlider;